/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { convertToUTCDate } from '@/utils/convertToUTCDate';
import EvalButton from '@/ux-common/src/components/evals/eval-button';
import { EvalJobType } from '@/ux-common/src/types/api';
import { RefreshCcwIcon } from 'lucide-react';
import { IPrompt } from 'types/prompt';
import { ViewByType } from 'types/traces';

import { cn } from '@/lib/utils';
import { useAppDetails } from '@/hooks/useAppDetails';
import { useEvals } from '@/hooks/useEvals';
import { useTraceId } from '@/hooks/useTraceID';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { CustomButton } from '../common';
import { FACT_NAME_LABEL_MAP } from '../display-page/fact-name-label-map';
import { ConversationIdSearch } from '../display-page/prompts/conversation-id-search';
import { DateRange } from '../display-page/prompts/date-range-picker/calendar-helper';
import {
  getPresetLabelToNameMap,
  getPresetRange,
  usePresets,
} from '../display-page/prompts/date-range-picker/calendar-helper/presets';
import { DateRangeUpdate } from '../display-page/prompts/date-range-picker/calendar-helper/types';
import { DateRangePicker } from '../display-page/prompts/date-range-picker/date-range';
import { TraceIdSearch } from '../display-page/traces/trace-id-search';
import { PromptPagination } from '../prompts-listing';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

type ToolbarProps = {
  //common props
  onToggle: () => void;
  iconSrc: string;
  setIsOverlayActive: Dispatch<SetStateAction<boolean>>;
  setActiveTab: Dispatch<SetStateAction<'time' | 'search'>>;
  activeTab: string;
  viewBy?: ViewByType;
  evaluateOpen?: boolean;
  setEvaluateOpen?: Dispatch<SetStateAction<boolean>>;
  handleApply?: (range: DateRangeUpdate) => void;
  factsOptions?: { key: string; label: string }[];

  //traces-props
  handleViewByChange?: (value: ViewByType) => void;
  isFromMeasuresBadge?: boolean;
  clearTraces?: () => void;
  handleClearErrorCategories?: () => void;
  handleRefresh?: () => void;
  setSelectedBar?: Dispatch<SetStateAction<string | null>>;
  isTracesPage?: boolean;
  supportedViews?: ViewByType[];

  //prompts-props
  handleDateRangeChange?: (range: DateRange) => void;
  isPromptsPage?: boolean;
  currentPage?: number;
  setCurrentPage?: Dispatch<SetStateAction<number>>;
  totalPages?: number;
  promptList?: any;
  itemsPerPage?: number;
  toggleHistogram?: () => void;
  selectedFact?: string;
  onSelectFact?: (value: string) => void;
  isLoading?: boolean;
  isFactsLoading?: boolean;
  traceId?: string | null;
  setTraceId?: Dispatch<SetStateAction<string | null>>;
  setSelectedPrompt?: Dispatch<SetStateAction<IPrompt[]>>;
  handleFormSubmit?: any;
  hidePagination?: boolean;

  onRefreshWithErrorRecovery?: (updatedTime: any) => void;
  mostRecent?: number | null;
};

const tabArr = ['time', 'search'];

export const ToolbarContainer: React.FC<ToolbarProps> = ({
  onToggle,
  iconSrc,
  handleDateRangeChange,
  setIsOverlayActive,
  setActiveTab,
  activeTab,
  isPromptsPage,
  currentPage,
  setCurrentPage,
  totalPages,
  promptList,
  itemsPerPage,
  selectedFact,
  onSelectFact,
  factsOptions,
  isLoading,
  isFactsLoading,
  handleApply,
  isTracesPage,
  viewBy,
  handleViewByChange,
  setEvaluateOpen,
  traceId,
  setTraceId,
  setSelectedPrompt,
  handleFormSubmit,
  isFromMeasuresBadge,
  onRefreshWithErrorRecovery,
  setSelectedBar,
  clearTraces,
  handleClearErrorCategories,
  handleRefresh,
  supportedViews,
  hidePagination,
  mostRecent,
}) => {
  const [dateRange, setDateRange] = useState<DateRange>();
  const { messages } = useLanguage();
  const presets = usePresets();
  const langData = messages?.General;
  const hasPrompts = Array.isArray(promptList) && promptList.length > 0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { clearTraceIDResults } = useTraceId();
  const isComponentPath = pathname.includes('/components/');
  const isAppPath = pathname.includes('/apps/');
  const factsForComponents = [
    { key: 'traces', label: messages?.General.traces },
    { key: 'inferences', label: messages?.General.inferences },
  ];
  const appData = useAppDetails();

  const data = isAppPath ? appData.data : undefined;
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const { state } = useEvals();
  const evalJobs: EvalJobType[] = state.evalJobs;
  const runningJobsCount = evalJobs.filter(
    (cur) => cur.status === 'RUNNING'
  ).length;

  // Remove trace_id from URL search params
  const clearTraceIdParam = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('trace_id')) {
      params.delete('trace_id');
      window.history.replaceState(null, '', `?${params.toString()}`);
    }
  };

  const showEvalBtn =
    activeTab === 'time'
      ? true
      : activeTab === 'search' && traceId
        ? true
        : false;

  const showEvalAfterFactName = isTracesPage
    ? isTracesPage
    : isComponentPath
      ? true // components (workflows) have no facts dropdown; show once the page is up
      : factsOptions && factsOptions.length > 0; //show the eval button after the traces, inference dropdown is loaded

  useEffect(() => {
    if (isPromptsPage) setActiveTab('time');
  }, [selectedFact]);

  // Set initial date range on component mount
  useEffect(() => {
    if (isAppPath || isComponentPath) {
      const start = searchParams.get('start');
      const end = searchParams.get('end');
      const days = searchParams.get('days');
      if (days === 'all') {
        const range = {
          from: undefined,
          to: undefined,
        };
        setDateRange(range);
        setActivePreset(null);
        return;
      }
      if (start && end) {
        const range = {
          from: new Date(start),
          to: new Date(end),
        };
        setDateRange(range);
        setActivePreset(null);
        return;
      }

      const presetMap = getPresetLabelToNameMap(presets);
      const defaultPresetName = presetMap[String(data?.default_time_range)];
      const range = getPresetRange(defaultPresetName, presets);
      setDateRange(range);
      setActivePreset(defaultPresetName);
    }
  }, []);

  const handleTabChange = (tab: 'time' | 'search') => {
    if (tab === activeTab) return;
    setTraceId?.(null);
    setSelectedPrompt?.([]);

    if (activeTab === 'search' && tab === 'time') {
      clearTraceIdParam();
      clearTraceIDResults();

      // Reset to app's default time range
      if (data) {
        const presetMap = getPresetLabelToNameMap(presets);
        const range = getPresetRange(
          presetMap[String(data.default_time_range)],
          presets
        );
        setDateRange(range);
        handleDateRangeChange?.(range);
      }
    } else {
      clearTraces?.();
      handleClearErrorCategories?.();
    }
    setActiveTab(tab);
  };

  const handleRefreshClick = () => {
    // for most-recent mode, Re-fetch latest traces directly without recalculating date ranges
    if (mostRecent) {
      clearTraces?.();
      handleRefresh?.();
      return;
    }

    // Recalculate preset range on refresh to use the current time window and get an updated range;
    // for custom range, don't recalculate - use the existing selected range
    let finalRange = dateRange;
    if (activePreset) {
      finalRange = getPresetRange(activePreset, presets);
    }
    const updatedTime = {
      start_time: convertToUTCDate(finalRange?.from ? finalRange.from : null),
      end_time: convertToUTCDate(finalRange?.to ? finalRange.to : null),
    };

    if (isPromptsPage) {
      (updatedTime as any).refresh = Date.now();

      // Use error recovery handler if error exists
      if (onRefreshWithErrorRecovery) {
        onRefreshWithErrorRecovery(updatedTime);
      }
      handleFormSubmit?.(updatedTime);
      return;
    }

    if (isTracesPage) {
      // refresh traces with updated time range
      handleApply?.({
        range: finalRange || { from: undefined, to: undefined },
        refresh: Date.now(),
        isPreset: !!activePreset,
      });
      clearTraces?.();
      setSelectedBar?.(null);
    }
  };

  return (
    <div className="mt-4 flex h-12 items-center rounded-[4px] border border-border bg-card-00 px-3 py-2">
      {(isTracesPage || isPromptsPage) && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <CustomButton
                  className="h-8 w-8 shrink-0 rounded bg-transparent p-0 hover:bg-[#257C9D1F] focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                  onClick={onToggle}
                  disabled={activeTab === 'search' || isFromMeasuresBadge}
                >
                  <Image
                    src={iconSrc}
                    alt="filter-icon"
                    width={24}
                    height={24}
                    priority
                  />
                </CustomButton>
              </div>
            </TooltipTrigger>
            {isFromMeasuresBadge && (
              <TooltipContent>
                <p>{messages?.General.change_time_box}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
      {((isTracesPage &&
        !isComponentPath &&
        factsOptions &&
        factsOptions.length > 0) ||
        (isPromptsPage && factsOptions && factsOptions.length > 0)) && (
        <>
          <Separator
            className="mx-4 h-full min-h-8 bg-line-tertiary px-px"
            orientation="vertical"
          />
          <div className="flex h-7 rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-1">
            <Select
              value={selectedFact}
              onValueChange={(value) =>
                onSelectFact && !isFactsLoading && onSelectFact(value)
              }
              disabled={isFactsLoading}
            >
              <SelectTrigger className="h-auto w-auto gap-1.5 border-none p-0 text-[13px] leading-[18px] text-body-subtle focus:ring-0 focus:ring-offset-0  [&>svg]:text-[#EBEFF1] [&>svg]:opacity-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="overflow-visible rounded-none bg-white text-[#201F1E]">
                {(isComponentPath
                  ? factsForComponents
                  : factsOptions || []
                ).map((option) => {
                  return (
                    <SelectItem
                      key={option.key}
                      value={option.key}
                      className="cursor-pointer focus:bg-[#EDEBE9] focus:text-[#201F1E]"
                    >
                      {option.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {(isTracesPage || isPromptsPage) && (
        <Separator
          className="mx-4 h-full min-h-8 bg-line-tertiary px-px"
          orientation="vertical"
        />
      )}

      <Tabs
        className="flex items-center justify-center gap-x-6"
        value={activeTab}
        onValueChange={(value) => handleTabChange(value as 'time' | 'search')}
      >
        {(isTracesPage || isPromptsPage) && (
          <TabsList className="bg-transparent p-0">
            {tabArr.map((tab, index) => {
              if (!selectedFact && index === 1 && isPromptsPage) return null; // Skip 'search' tab if no selectedFact
              return (
                <TabsTrigger
                  key={tab}
                  value={tab ?? ''}
                  className={`h-7 rounded-none border border-form-control-border-rest bg-canvas-background-secondary px-2 py-[11px] text-[11px] font-normal text-white first:rounded-l last:rounded-r data-[state=active]:border-2 data-[state=active]:border-link-active data-[state=active]:bg-[#07181E] data-[state=active]:font-medium`}
                >
                  {tab === 'time'
                    ? mostRecent
                      ? 'Most recent'
                      : langData?.time_range
                    : FACT_NAME_LABEL_MAP?.[selectedFact || '']?.idLabel ||
                      (selectedFact === 'conversations'
                        ? langData?.conversation_id
                        : selectedFact
                          ? `${selectedFact?.charAt(0).toUpperCase()}${selectedFact?.slice(1)} ${langData?.id}`
                          : langData?.trace_id)}
                </TabsTrigger>
              );
            })}
          </TabsList>
        )}
        <TabsContent
          className="mt-0 flex items-center gap-2 text-form-input-text text-form-input-rest"
          value={'time'}
        >
          {!isTracesPage && (
            <span className="text-form-input-text text-form-input-rest">
              {langData?.time_range}
            </span>
          )}
          <div className="flex h-7 items-center rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-[5px]">
            <DateRangePicker
              onRangeChange={handleDateRangeChange}
              classNames={{
                rangeTriggerClassName:
                  'border-none py-0 focus:ring-0 focus:ring-offset-0',
                triggerIconClassName: 'items-center gap-1.5',
                rangeClassName: cn(
                  'text-form-input-disabled text-[13px] font-normal items-center gap-1.5',
                  'flex-row'
                ),
              }}
              onUpdate={(payload) => {
                setDateRange(payload.range);
                setActivePreset(payload.presetName || null); // Track preset for refresh
                handleApply?.(payload);
              }}
              currentPage={isTracesPage ? 'traces' : 'prompts'}
              initialRange={dateRange}
              factName={selectedFact}
            />
          </div>
          <button className="text-white">
            <RefreshCcwIcon
              className="size-4 text-[#257C9D]"
              onClick={handleRefreshClick}
            />
          </button>
        </TabsContent>
        <TabsContent className="mt-0" value="search">
          {selectedFact !== 'conversations' ? (
            <TraceIdSearch
              setIsOverlayActive={setIsOverlayActive}
              isPromptSearch={!!isPromptsPage}
              initialValue={searchParams.get('trace_id') || ''}
              selectedFact={selectedFact}
              setTraceId={setTraceId}
              activeTab={activeTab}
            />
          ) : (
            <ConversationIdSearch
              setIsOverlayActive={setIsOverlayActive}
              isPromptSearch={true}
              initialValue={searchParams.get('conversation_id') || ''}
            />
          )}
        </TabsContent>
      </Tabs>
      {(!isComponentPath || isTracesPage || isPromptsPage) &&
        showEvalBtn &&
        showEvalAfterFactName && (
          <EvalButton
            title={messages?.Evaluation.evaluate ?? ''}
            tooltipDisabled={messages?.Evaluation.disabled_tooltip ?? ''}
            evaluateTooltip={messages?.Evaluation.evaluate_tooltip ?? ''}
            setEvaluateOpen={setEvaluateOpen}
            runningJobsCount={runningJobsCount}
            isFactsLoading={isFactsLoading}
          />
        )}

      <div className="ml-auto flex h-7 items-center text-white">
        {isPromptsPage &&
          hasPrompts &&
          !hidePagination &&
          !isLoading &&
          currentPage !== undefined &&
          setCurrentPage &&
          totalPages !== undefined &&
          itemsPerPage !== undefined && (
            <PromptPagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={
                selectedFact !== 'inferences' ? promptList.length : totalPages
              }
              promptList={promptList}
              itemsPerPage={itemsPerPage}
              selectedFact={
                selectedFact && selectedFact !== 'inferences'
                  ? selectedFact
                  : 'inferences'
              }
            />
          )}

        {isTracesPage && !mostRecent && (
          <div className="ml-auto flex items-center">
            {supportedViews &&
              supportedViews.length > 1 &&
              supportedViews.map((view) => (
                <button
                  key={view}
                  value={view}
                  className={cn(
                    'h-7 border border-form-control-border-rest bg-secondary px-2.5 text-[11px] font-normal capitalize leading-4 text-body-secondary hover:bg-secondary active:border-link-active active:bg-[#07181E] active:font-medium active:text-body-primary',
                    viewBy === view &&
                      'border-2 border-link-active bg-[#07181E] font-medium text-body-primary hover:bg-[#07181E]'
                  )}
                  onClick={() => handleViewByChange?.(view)}
                >
                  {view === 'all'
                    ? langData?.all
                    : view === 'performance'
                      ? langData?.performance
                      : langData?.reliability}
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
